/**
 * Tickets (support) API – customer-facing subset. Mirrors
 * vin-pulse-os-web/src/services/ticketsApi.ts.
 *
 * Backend (routes/tickets.ts):
 *   GET  /api/v1/tickets      – list; Customer requests are silently scoped
 *                                server-side to their own customer_id
 *   GET  /api/v1/tickets/:id  – detail; 403 if not the caller's own ticket
 *   POST /api/v1/tickets      – create; Customer-role posts auto-assign
 *                                customer_id + source=customer_portal server-side
 */

import { apiClient } from './apiClient';

export type TicketCategory =
  | 'customer' | 'delivery' | 'payment' | 'inventory'
  | 'compliance' | 'returns' | 'other';

export type TicketPriority = 'low' | 'medium' | 'high' | 'critical';

export type TicketStatus =
  | 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed'
  | 'pending_review' | 'approved' | 'rejected';

export type TicketSource =
  | 'customer_portal' | 'system_alert' | 'driver_app'
  | 'warehouse_staff' | 'call_center' | 'other';

export interface TicketMessage {
  id: number;
  ticket_id: number;
  sender_user_id: number | null;
  sender_name: string | null;
  sender_role: 'customer' | 'support' | 'system';
  body: string;
  is_internal: boolean;
  created_at: string;
}

export interface Ticket {
  id: number;
  ticket_number: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  subject: string;
  description: string | null;
  customer_id: number | null;
  customer_name: string | null;
  order_id: number | null;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  created_by_user_id: number;
  created_by_name: string | null;
  source: TicketSource;
  resolution_notes: string | null;
  sla_due_at: string | null;
  opened_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  messages?: TicketMessage[];
  created_at: string;
  updated_at: string;
}

export interface ListTicketsFilters {
  page?: number;
  limit?: number;
  category?: TicketCategory;
  priority?: TicketPriority;
  status?: TicketStatus;
}

export interface CreateTicketInput {
  category: TicketCategory;
  priority?: TicketPriority;
  subject: string;
  description?: string;
  order_id?: number;
}

export interface PaginatedTickets {
  data: Ticket[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export async function listTickets(filters: ListTicketsFilters = {}): Promise<PaginatedTickets> {
  return apiClient.get<PaginatedTickets>('/tickets', filters as Record<string, any>);
}

export async function getTicket(id: number | string): Promise<Ticket> {
  return apiClient.get<Ticket>(`/tickets/${id}`);
}

export async function createTicket(input: CreateTicketInput): Promise<Ticket> {
  return apiClient.post<Ticket>('/tickets', input);
}
